/**
 * ComposerModeControl — the mode chip in the chat composer.
 *
 * The panel is a choice of two modes and, for Extended thinking, a four-step
 * effort scale drawn as triangles that fill up to the chosen level. What a
 * level DOES is its hover hint — never a price, because price depends
 * entirely on which models the team runs (a team on their own keys pays
 * nothing).
 */
import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Check } from "lucide-react";

export const EFFORT_LEVELS = [
  { key: "low", label: "Low", hint: "A focused pass over the most relevant runs. Minutes." },
  { key: "medium", label: "Medium", hint: "Broader investigation with re-planning between waves." },
  { key: "high", label: "High", hint: "Reads widely across the corpus, cross-checks its findings." },
  { key: "max", label: "Max", hint: "Exhaustive. Every relevant run and span, as long as it takes." },
];

export default function ComposerModeControl({
  extended, onExtendedChange, effort, onEffortChange, disabled, placement = "above",
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const effortIndex = Math.max(0, EFFORT_LEVELS.findIndex((l) => l.key === effort));
  const current = EFFORT_LEVELS[effortIndex] || EFFORT_LEVELS[1];

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label="Analysis mode"
        className="h-8 rounded-lg flex items-center gap-1.5 px-1 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:rgb(var(--trodo-primary-rgb)/0.25)]"
        style={{ color: "var(--text-primary)" }}
      >
        <span>{extended ? `Extended · ${current.label}` : "Analyze"}</span>
        <ChevronDown size={12} strokeWidth={2} className={open ? "rotate-180 transition-transform" : "transition-transform"} />
      </button>

      {open && (
        <div
          className={`absolute left-0 z-30 w-56 rounded-xl border p-2 ${placement === "above" ? "bottom-full mb-2" : "top-full mt-2"}`}
          // The same surface and shadow every brand menu uses (BrandDropdown),
          // so the panel reads as one of them in both themes.
          style={{ borderColor: "var(--border-default)", backgroundColor: "var(--surface-raised)", boxShadow: "var(--shadow-lg)" }}
          role="dialog"
          aria-label="Analysis mode options"
        >
          <ModeRow
            selected={!extended}
            title="Analyze"
            onClick={() => onExtendedChange(false)}
          />
          <ModeRow
            selected={extended}
            title="Extended thinking"
            onClick={() => onExtendedChange(true)}
          />

          {extended && (
            <div className="mt-1 mx-1 pt-3 pb-1 px-1 border-t" style={{ borderColor: "var(--border-default)" }}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-[0.12em]" style={{ color: "var(--text-muted)" }}>Effort</span>
                <span className="text-[12px] font-medium" style={{ color: "var(--trodo-primary)" }}>{current.label}</span>
              </div>
              <div className="mt-2 flex items-end justify-between" role="radiogroup" aria-label="Effort level">
                {EFFORT_LEVELS.map((l, i) => (
                  <button
                    key={l.key}
                    type="button"
                    role="radio"
                    aria-checked={i === effortIndex}
                    aria-label={l.label}
                    title={l.hint}
                    onClick={() => onEffortChange(l.key)}
                    className="flex flex-1 flex-col items-center gap-1 rounded-md py-1 transition-colors hover:bg-surface-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:rgb(var(--trodo-primary-rgb)/0.25)]"
                  >
                    <Tri size={10 + i * 3} on={i <= effortIndex} />
                    <span className="text-[11px]" style={{ color: i === effortIndex ? "var(--text-primary)" : "var(--text-muted)", fontWeight: i === effortIndex ? 500 : 400 }}>{l.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* The brand's triangle: ember when on, a faint outline-tint when off. */
function Tri({ size, on }) {
  return (
    <svg aria-hidden width={size} height={Math.round(size * 0.9)} viewBox="0 0 80 72" className="shrink-0 transition-colors">
      <polygon points="40,4 76,68 4,68" style={{ fill: on ? "var(--trodo-primary)" : "rgb(var(--trodo-primary-rgb) / 0.16)" }} />
    </svg>
  );
}

function ModeRow({ selected, title, onClick }) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={selected}
      onClick={onClick}
      className="w-full flex items-center justify-between gap-3 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-surface-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:rgb(var(--trodo-primary-rgb)/0.25)]"
    >
      <span className="text-[13px] font-medium" style={{ color: "var(--text-primary)" }}>{title}</span>
      <Check size={15} strokeWidth={2.5} className="shrink-0" style={{ color: "var(--text-primary)", opacity: selected ? 1 : 0 }} aria-hidden="true" />
    </button>
  );
}
